import { Helmet } from "react-helmet-async";
import { Panel, DefaultButton, Stack, PanelType, Dialog, DialogType, DialogFooter, PrimaryButton } from "@fluentui/react";
import { People24Regular } from "@fluentui/react-icons";
import { Button } from "@fluentui/react-components";

import styles from "./Chat.module.css";

import { Answer, AnswerError, AnswerLoading } from "../../components/Answer";
import { QuestionInput } from "../../components/QuestionInput";
import { ExampleList } from "../../components/Example";
import { UserChatMessage } from "../../components/UserChatMessage";
import { AnalysisPanel, AnalysisPanelTabs } from "../../components/AnalysisPanel";
import { SettingsButton } from "../../components/SettingsButton";
import { ClearChatButton } from "../../components/ClearChatButton";
import { UploadFile } from "../../components/UploadFile/UploadFile";
import { HistoryPanel } from "../../components/HistoryPanel";
import { HistoryButton } from "../../components/HistoryButton";
import { TokenClaimsDisplay } from "../../components/TokenClaimsDisplay";
import { Settings } from "../../components/Settings/Settings";
import { ClinicalSettings } from "../../components/ClinicalSettings/ClinicalSettings";
import { TokenBudgetIndicator } from "../../components/TokenBudgetIndicator";
import { LanguagePicker } from "../../i18n/LanguagePicker";

import { useChatSession } from "../../hooks/useChatSession";

function formatSelectedScopeCount(selectedCount: number, filteredCount?: number): string {
    return filteredCount && filteredCount >= selectedCount ? `${selectedCount} of ${filteredCount}` : String(selectedCount);
}

const Chat = () => {
    const s = useChatSession();
    const selectedScopeFilteredCount = s.selectedNoteScope.filteredNoteCount ?? s.tokenEstimate?.noteCount;
    const selectedScopeCount = s.selectedNoteScope.noteIds.length;
    const selectedNoteScopeIsNarrowed =
        s.selectedNoteScope.mode === "selected_notes" &&
        selectedScopeCount > 0 &&
        (typeof selectedScopeFilteredCount !== "number" || selectedScopeFilteredCount <= 0 || selectedScopeCount < selectedScopeFilteredCount);
    const selectedAnalysisAnswer = s.answers[s.selectedAnswer]?.[1];

    return (
        <div className={styles.container}>
            <Helmet>
                <title>{s.t("pageTitle")}</title>
            </Helmet>
            <div
                className={styles.commandsSplitContainer}
                style={{ marginLeft: s.isClinicalPanelOpen || s.isClinicalPanelPinned ? "320px" : "0", transition: "margin-left 0.3s ease" }}
            >
                <div className={styles.commandsContainer}>
                    {(s.showChatHistoryCosmos || s.showChatHistoryBrowser) && (
                        <HistoryButton
                            className={`${styles.commandButton} ${s.isClinicalPanelOpen ? styles.historyButtonShifted : ""}`}
                            onClick={() => s.setIsHistoryPanelOpen(!s.isHistoryPanelOpen)}
                        />
                    )}
                </div>
                <div className={styles.commandsContainer}>
                    <ClearChatButton className={styles.commandButton} onClick={s.clearChat} disabled={!s.lastQuestionRef.current || s.isLoading} />
                    {s.showUserUpload && <UploadFile className={styles.commandButton} disabled={!s.loggedIn} />}
                    <div className={styles.commandButton} style={{ display: "flex", alignItems: "center" }}>
                        <Button appearance="transparent" icon={<People24Regular />} onClick={() => s.setIsClinicalPanelOpen(!s.isClinicalPanelOpen)}>
                            Clinical Settings
                        </Button>
                    </div>

                    <SettingsButton className={styles.commandButton} onClick={() => s.setIsConfigPanelOpen(!s.isConfigPanelOpen)} />
                </div>
            </div>
            <div className={styles.chatRoot} style={{ marginLeft: s.isClinicalPanelPinned || s.isHistoryPanelOpen ? "320px" : "0" }}>
                <div className={styles.chatContainer}>
                    {s.selectedNoteScope.status === "active" && selectedNoteScopeIsNarrowed && (
                        <div className={styles.scopeBanner} aria-label="Chat note scope">
                            <div>
                                <strong>
                                    {`Chat scope: ${formatSelectedScopeCount(selectedScopeCount, selectedScopeFilteredCount)} selected Browse ${selectedScopeCount === 1 ? "note" : "notes"} only`}
                                </strong>
                                <small>Answers and citations are constrained by this Browse scope. Retrieval details appear in Thought Process after a question.</small>
                            </div>
                            <div className={styles.scopeBannerActions}>
                                <DefaultButton text="Clear scope" onClick={s.clearSelectedNoteScope} />
                            </div>
                        </div>
                    )}
                    {s.selectedNoteScope.status === "broadened" && selectedNoteScopeIsNarrowed && (
                        <div className={styles.scopeBanner} aria-label="Chat note scope">
                            <span>Selected-note focus is paused; Chat is using broader patient context.</span>
                            <div className={styles.scopeBannerActions}>
                                <DefaultButton text="Clear scope" onClick={s.clearSelectedNoteScope} />
                            </div>
                        </div>
                    )}
                    {s.selectedNoteScope.status === "conflict" && selectedNoteScopeIsNarrowed && (
                        <div className={styles.scopeBannerWarning} aria-label="Chat note scope conflict">
                            <span>Selected-note scope no longer matches the current patient. Clear scope or return to Browse.</span>
                            <DefaultButton text="Clear scope" onClick={s.clearSelectedNoteScope} />
                        </div>
                    )}
                    {s.selectedNoteScope.status === "stale" && selectedNoteScopeIsNarrowed && (
                        <div className={styles.scopeBannerWarning} aria-label="Chat note scope stale">
                            <span>Selected-note scope needs refresh for the current Browse filters. Clear scope or return to Browse before asking.</span>
                            <div className={styles.scopeBannerActions}>
                                <DefaultButton text="Clear scope" onClick={s.clearSelectedNoteScope} />
                            </div>
                        </div>
                    )}
                    {!s.lastQuestionRef.current ? (
                        <div className={styles.chatEmptyState}>
                            <h1 className={styles.chatEmptyStateTitle}>
                                {s.clinicalFilters.patientName ? <>{s.clinicalFilters.patientName} &mdash; Clinical Notes</> : s.t("chatEmptyStateTitle")}
                            </h1>
                            {s.useBudgetMonitor && (
                                <TokenBudgetIndicator
                                    estimate={s.tokenEstimate}
                                    isLoading={s.isEstimatingTokens}
                                    compact={true}
                                    onOptionSelect={s.handleBudgetOptionSelect}
                                    onOpenDeveloperSettings={() => s.setIsConfigPanelOpen(true)}
                                    onOpenClinicalSettings={() => s.setIsClinicalPanelOpen(true)}
                                />
                            )}
                            <h2 className={styles.chatEmptyStateSubtitle}>{s.t("chatEmptyStateSubtitle")}</h2>
                            {s.showLanguagePicker && <LanguagePicker onLanguageChange={newLang => s.i18n.changeLanguage(newLang)} />}

                            <ExampleList onExampleClicked={s.onExampleClicked} useMultimodalAnswering={s.showMultimodalOptions} />
                        </div>
                    ) : (
                        <div className={styles.chatMessageStream} ref={s.chatMessageStreamContainer} onScroll={s.onChatMessageStreamScroll}>
                            {s.isStreaming &&
                                s.streamedAnswers.map((streamedAnswer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={streamedAnswer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={true}
                                                key={index}
                                                answer={streamedAnswer[1]}
                                                index={index}
                                                speechConfig={s.speechConfig}
                                                isSelected={false}
                                                onCitationClicked={c => s.onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => s.onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => s.onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onEvidenceGraphClicked={() => s.onToggleTab(AnalysisPanelTabs.EvidenceGraphTab, index)}
                                                onFollowupQuestionClicked={q => s.makeApiRequest(q)}
                                                showFollowupQuestions={s.useSuggestFollowupQuestions && s.streamedAnswers.length - 1 === index}
                                                showSpeechOutputAzure={s.showSpeechOutputAzure}
                                                showSpeechOutputBrowser={s.showSpeechOutputBrowser}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {!s.isStreaming &&
                                s.answers.map((answer, index) => (
                                    <div key={index}>
                                        <UserChatMessage message={answer[0]} />
                                        <div className={styles.chatMessageGpt}>
                                            <Answer
                                                isStreaming={false}
                                                key={index}
                                                answer={answer[1]}
                                                index={index}
                                                speechConfig={s.speechConfig}
                                                isSelected={s.selectedAnswer === index && s.activeAnalysisPanelTab !== undefined}
                                                onCitationClicked={c => s.onShowCitation(c, index)}
                                                onThoughtProcessClicked={() => s.onToggleTab(AnalysisPanelTabs.ThoughtProcessTab, index)}
                                                onSupportingContentClicked={() => s.onToggleTab(AnalysisPanelTabs.SupportingContentTab, index)}
                                                onEvidenceGraphClicked={() => s.onToggleTab(AnalysisPanelTabs.EvidenceGraphTab, index)}
                                                onFollowupQuestionClicked={q => s.makeApiRequest(q)}
                                                showFollowupQuestions={s.useSuggestFollowupQuestions && s.answers.length - 1 === index}
                                                showSpeechOutputAzure={s.showSpeechOutputAzure}
                                                showSpeechOutputBrowser={s.showSpeechOutputBrowser}
                                            />
                                        </div>
                                    </div>
                                ))}
                            {s.isLoading && (
                                <>
                                    <UserChatMessage message={s.lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerLoading />
                                    </div>
                                </>
                            )}
                            {s.error ? (
                                <>
                                    <UserChatMessage message={s.lastQuestionRef.current} />
                                    <div className={styles.chatMessageGptMinWidth}>
                                        <AnswerError error={s.error.toString()} onRetry={() => s.makeApiRequest(s.lastQuestionRef.current)} />
                                    </div>
                                </>
                            ) : null}
                            <div ref={s.chatMessageStreamEnd} />
                        </div>
                    )}

                    <div className={styles.chatInput}>
                        <QuestionInput
                            clearOnSend
                            placeholder={s.t("defaultExamples.placeholder")}
                            disabled={s.isLoading}
                            onSend={question => s.makeApiRequest(question)}
                            showSpeechInput={s.showSpeechInput}
                        />
                    </div>
                </div>

                {selectedAnalysisAnswer && s.activeAnalysisPanelTab && (
                    <AnalysisPanel
                        className={styles.chatAnalysisPanel}
                        activeCitation={s.activeCitation}
                        onActiveTabChanged={x => s.onToggleTab(x, s.selectedAnswer)}
                        citationHeight="810px"
                        answer={selectedAnalysisAnswer}
                        activeTab={s.activeAnalysisPanelTab}
                        onCitationClicked={c => s.onShowCitation(c, s.selectedAnswer)}
                    />
                )}

                {(s.showChatHistoryCosmos || s.showChatHistoryBrowser) && (
                    <HistoryPanel
                        provider={s.historyProvider}
                        isOpen={s.isHistoryPanelOpen}
                        notify={!s.isStreaming && !s.isLoading}
                        onClose={() => s.setIsHistoryPanelOpen(false)}
                        onChatSelected={answers => s.restoreChat(answers)}
                    />
                )}

                <Panel
                    headerText={s.t("labels.headerText")}
                    isOpen={s.isConfigPanelOpen}
                    isBlocking={false}
                    onDismiss={() => s.setIsConfigPanelOpen(false)}
                    closeButtonAriaLabel={s.t("labels.closeButton")}
                    onRenderFooterContent={() => <DefaultButton onClick={() => s.setIsConfigPanelOpen(false)}>{s.t("labels.closeButton")}</DefaultButton>}
                    isFooterAtBottom={true}
                >
                    <Settings
                        promptTemplate={s.promptTemplate}
                        temperature={s.temperature}
                        retrieveCount={s.clinicalFilters.topResults}
                        agenticReasoningEffort={s.agenticReasoningEffort}
                        seed={s.seed}
                        minimumSearchScore={s.minimumSearchScore}
                        minimumRerankerScore={s.minimumRerankerScore}
                        useSemanticRanker={s.useSemanticRanker}
                        useSemanticCaptions={s.contentMode === "semantic"}
                        useQueryRewriting={s.useQueryRewriting}
                        reasoningEffort={s.reasoningEffort}
                        excludeCategory={s.excludeCategory}
                        includeCategory={s.includeCategory}
                        retrievalMode={s.retrievalMode}
                        showMultimodalOptions={s.showMultimodalOptions}
                        sendTextSources={s.sendTextSources}
                        sendImageSources={s.sendImageSources}
                        searchTextEmbeddings={s.searchTextEmbeddings}
                        searchImageEmbeddings={s.searchImageEmbeddings}
                        showSemanticRankerOption={s.showSemanticRankerOption}
                        showQueryRewritingOption={s.showQueryRewritingOption}
                        showReasoningEffortOption={s.showReasoningEffortOption}
                        showVectorOption={s.showVectorOption}
                        useLogin={s.useLogin}
                        loggedIn={s.loggedIn}
                        requireAccessControl={s.requireAccessControl}
                        shouldStream={s.shouldStream}
                        streamingEnabled={s.streamingEnabled}
                        useSuggestFollowupQuestions={s.useSuggestFollowupQuestions}
                        showAgenticRetrievalOption={s.showAgenticRetrievalOption}
                        useAgenticKnowledgeBase={s.useAgenticKnowledgeBase}
                        useBudgetMonitor={s.useBudgetMonitor}
                        contextPerspective={s.contextPerspective}
                        retrievalEffort={s.retrievalEffort}
                        useLangGraphClinicalRetrieval={s.useLangGraphClinicalRetrieval}
                        clinicalChatGraphMode={s.clinicalChatGraphMode}
                        clinicalChatQueryRewriteMode={s.clinicalChatQueryRewriteMode}
                        clinicalChatRuntimePlannerMode={s.clinicalChatRuntimePlannerMode}
                        clinicalChatRerankerMode={s.clinicalChatRerankerMode}
                        showGraphSupplementalEvidenceOption={s.showGraphSupplementalEvidenceOption}
                        showGraphSupplementalEvidence={s.showGraphSupplementalEvidence}
                        useWebSource={s.webSourceEnabled}
                        showWebSourceOption={s.webSourceSupported}
                        useSharePointSource={s.sharePointSourceEnabled}
                        showSharePointSourceOption={s.sharePointSourceSupported}
                        hideMinimalRetrievalReasoningOption={s.hideMinimalRetrievalReasoningOption}
                        availableModels={s.availableModels}
                        selectedModel={s.selectedModel}
                        selectedTemplate={s.selectedTemplate}
                        onModelChange={model => s.handleSettingsChange("selectedModel", model)}
                        onTemplateChange={template => s.handleSettingsChange("selectedTemplate", template)}
                        page="chat"
                        onChange={s.handleSettingsChange}
                    />
                    {s.useLogin && <TokenClaimsDisplay />}
                </Panel>
                <Panel
                    headerText="Clinical Settings"
                    isOpen={s.isClinicalPanelOpen || s.isClinicalPanelPinned}
                    isBlocking={false}
                    isLightDismiss={false}
                    type={PanelType.smallFixedNear}
                    hasCloseButton={false}
                    onDismiss={() => {
                        s.setIsClinicalPanelPinned(false);
                        s.setIsClinicalPanelOpen(false);
                    }}
                    onRenderNavigation={() => (
                        <Stack horizontal horizontalAlign="end" styles={{ root: { width: "100%", padding: "10px 10px 0 0" } }}>
                            <DefaultButton
                                iconProps={{ iconName: "Unpin" }}
                                title="Unpin and close"
                                onClick={() => {
                                    s.setIsClinicalPanelPinned(false);
                                    s.setIsClinicalPanelOpen(false);
                                }}
                            />
                        </Stack>
                    )}
                >
                    <ClinicalSettings
                        filters={s.clinicalFilters}
                        onFiltersChange={newFilters => {
                            const facetsChanged =
                                JSON.stringify(newFilters.noteTypes) !== JSON.stringify(s.clinicalFilters.noteTypes) ||
                                JSON.stringify(newFilters.formNames) !== JSON.stringify(s.clinicalFilters.formNames) ||
                                JSON.stringify(newFilters.clinicalActivities) !== JSON.stringify(s.clinicalFilters.clinicalActivities) ||
                                JSON.stringify(newFilters.assessmentScales) !== JSON.stringify(s.clinicalFilters.assessmentScales) ||
                                JSON.stringify(newFilters.servicePrograms) !== JSON.stringify(s.clinicalFilters.servicePrograms) ||
                                JSON.stringify(newFilters.serviceDescriptions) !== JSON.stringify(s.clinicalFilters.serviceDescriptions) ||
                                JSON.stringify(newFilters.providers) !== JSON.stringify(s.clinicalFilters.providers) ||
                                JSON.stringify(newFilters.caseManagers) !== JSON.stringify(s.clinicalFilters.caseManagers) ||
                                JSON.stringify(newFilters.assessmentStaff) !== JSON.stringify(s.clinicalFilters.assessmentStaff) ||
                                JSON.stringify(newFilters.dateRanges) !== JSON.stringify(s.clinicalFilters.dateRanges) ||
                                JSON.stringify(newFilters.severityIndicators) !== JSON.stringify(s.clinicalFilters.severityIndicators) ||
                                JSON.stringify(newFilters.serviceCodes) !== JSON.stringify(s.clinicalFilters.serviceCodes);

                            if (facetsChanged) {
                                s.confirmSettingChange("Filters", () => s.setClinicalFilters(newFilters));
                            } else {
                                s.setClinicalFilters(newFilters);
                            }
                        }}
                    />
                </Panel>
            </div>

            {/* Settings change confirmation dialog */}
            <Dialog
                hidden={!s.pendingSettingChange}
                onDismiss={() => s.handleConfirmContinue()}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: "Chat in Progress",
                    subText: `You're changing ${s.pendingSettingChange?.type}. Would you like to clear the current chat or continue with the existing conversation?`
                }}
                modalProps={{ isBlocking: true }}
            >
                <DialogFooter>
                    <PrimaryButton onClick={s.handleConfirmClearChat} text="Clear Chat" />
                    <DefaultButton onClick={s.handleConfirmContinue} text="Continue" />
                </DialogFooter>
            </Dialog>
        </div>
    );
};

export default Chat;
